import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const rawSiteUrl: string =
  typeof import.meta.env.VITE_SITE_URL === "string"
    ? import.meta.env.VITE_SITE_URL
    : "";

const siteOrigin = rawSiteUrl.replace(/\/+$/, "");

interface PageSeo {
  title: string;
  description: string;
  keywords: string;
  path: string;
  image?: string;
}

const HOME_SEO: PageSeo = {
  title: "Bertie's Restaurant & Cocktails",
  description:
    "Bertie's Restaurant & Cocktails serves authentic Caribbean cuisine and craft cocktails in Brandon, Florida. Explore our menu, weekend brunch, and reserve your table today.",
  keywords:
    "Caribbean restaurant Brandon Florida, Bertie's Restaurant & Cocktails, Caribbean cuisine, craft cocktails, weekend brunch, Brandon FL dining",
  path: "/",
  image:
    "https://readdy.ai/api/search-image?query=Dark%20elegant%20Caribbean%20restaurant%20interior%20at%20night%20with%20warm%20golden%20candlelight%20and%20crystal%20chandeliers%2C%20rich%20wood%20tables%2C%20moody%20ambient%20lighting%2C%20luxury%20dining%20atmosphere%2C%20deep%20charcoal%20and%20gold%20tones%2C%20cinematic%20photography&width=1600&height=900&seq=berties-hero-bg&orientation=landscape",
};

const SEO_BY_ROUTE: Record<string, PageSeo> = {
  "/": HOME_SEO,
};

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const seo = SEO_BY_ROUTE[location.pathname];
    if (!seo) {
      return;
    }

    const canonical = `${siteOrigin}${seo.path}`;

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertCanonical(canonical);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonical);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);

    if (seo.image) {
      upsertMeta("property", "og:image", seo.image);
      upsertMeta("name", "twitter:image", seo.image);
    }
  }, [location.pathname]);

  return null;
}